import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { useMemo, useState } from "react";
import {
    useDataProvidersApi,
    useEmissionRecordsApiClient,
} from "../../../../../hooks";
import { CustomSwitch } from "../../../../../ui/CustomSwitch";
import { useDrawAndFilterControlsV2 } from "../../../hooks/areaFilter";
import { useMapData, useMapFilters } from "../../../hooks/mapDataAndFilters";
import { useMap } from "../../../hooks/mapState";
import { InsightsContainer } from "../../../ui/MapControlsV2";
import { type TreemapCardCategory, TreemapCardView } from "./ui";

export const BridgerEmissionInsights = () => {
    const dataProvidersApiClient = useDataProvidersApi();
    const emissionRecordsApiClient = useEmissionRecordsApiClient();
    const [plotMode, setPlotMode] = useState<"count" | "total_volume">("count");
    const { debounced } = useMap("mainMap");
    const { selectedFilters, filterData, areaToFetch } =
        useDrawAndFilterControlsV2("mainMap");
    const { filterState } = useMapFilters("mainMap");
    const { selectedContext } = useMapData("mainMap");

    // Fetch data providers, and check if Bridger is enabled.
    const bridgerQuery = useQuery({
        queryKey: ["lookingForBridgerQuery"],
        queryFn: async () => {
            const response = await dataProvidersApiClient.dataProvidersList({
                hasData: true,
                dataSource: ["SELF_REPORTED"],
            });
            return response.results;
        },
        refetchOnWindowFocus: false,
        staleTime: 3600,
    });

    const isBridgerEnabled = useMemo(() => {
        const bridger = bridgerQuery.data?.find((p) => p.name === "Bridger");

        // No bridger data
        if (!bridger) {
            return false;
        }

        // If all providers are enabled.
        if (
            filterState.emissions.emissionGroupFiltersV2.selfReported === "all"
        ) {
            return true;
        }

        // Else check if bridger is enabled.
        return !!filterState.emissions.emissionGroupFiltersV2.selfReported.find(
            (p) => p.id === bridger.id,
        );
    }, [bridgerQuery, filterState.emissions.emissionGroupFiltersV2]);

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore =
                filterState.emissions.endDateFilter !== ""
                    ? new Date(filterState.emissions.endDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.end) {
                dateBefore = new Date(selectedContext.dateRangeToShowData.end);
            }
            let dateAfter =
                filterState.emissions.startDateFilter !== ""
                    ? new Date(filterState.emissions.startDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.start) {
                dateAfter = new Date(selectedContext.dateRangeToShowData.start);
            }
            return { dateBefore, dateAfter };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            selectedContext.dateRangeToShowData,
        ]),
        400,
    );

    const bridgerStats = useQuery({
        queryKey: [
            "bridgerStats",
            areaToFetch,
            filterState.emissions,
            debouncedDateRange,
        ],
        queryFn: async () => {
            if (filterState.emissions.eventStatus.length === 0) {
                return [];
            }
            return await emissionRecordsApiClient.emissionRecordsBridgerStatsList(
                {
                    dataSource: ["SELF_REPORTED"],
                    eventStatus: filterState.emissions.eventStatus,
                    detectionDateRangeAfter:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    detectionDateRangeBefore:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    createdAtAfter:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    createdAtBefore:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    detectedRateMin: filterState.emissions.detectedRateMin
                        ? filterState.emissions.detectedRateMin * 1000
                        : undefined,
                    detectedRateMax: filterState.emissions.detectedRateMax
                        ? filterState.emissions.detectedRateMax * 1000
                        : undefined,
                    locationWithin: areaToFetch,
                    locationPreset:
                        filterData && selectedFilters.length > 0
                            ? selectedFilters
                            : undefined,
                },
            );
        },
        enabled: isBridgerEnabled && !!debounced.areaOnScreen,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        staleTime: 0,
    });

    const categories = useMemo((): TreemapCardCategory[] => {
        return (bridgerStats.data || []).map((d) => ({
            key: d.equipmentName,
            label: d.equipmentName,
            // Volume is reported in kg/h, while rates are stored in g/h.
            count:
                plotMode === "count"
                    ? d.emissionCount
                    : Math.round(d.emissionSum / 1000),
        }));
    }, [bridgerStats.data, plotMode]);

    // The treemap only plots positive counts, so match that when deciding
    // whether there is anything to show.
    const hasData = categories.some((d) => d.count > 0);

    if (!filterState.emissions.showEmissions || !isBridgerEnabled) {
        return null;
    }

    return (
        <InsightsContainer>
            <div className="flex items-center justify-between">
                <p className="font-bold text-sm">Equipment Emission Insights</p>
                <div className="flex items-center">
                    <span className="text-xs text-gray-600 mr-1">
                        Count/volume
                    </span>
                    <CustomSwitch
                        size="xs"
                        checked={plotMode === "total_volume"}
                        onChange={() =>
                            setPlotMode((v) =>
                                v === "count" ? "total_volume" : "count",
                            )
                        }
                    />
                </div>
            </div>
            {hasData ? (
                <TreemapCardView
                    data={categories}
                    loading={bridgerQuery.isFetching || bridgerStats.isFetching}
                    totalLabel={
                        plotMode === "count" ? "detections" : "kg/h total"
                    }
                    valueSuffix={plotMode === "count" ? undefined : "kg/h"}
                />
            ) : (
                <div className="text-xs text-ae-slate-600 py-4 text-center">
                    No data to display.
                </div>
            )}
        </InsightsContainer>
    );
};
